import {
    HttpContextToken,
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import * as Sentry from "@sentry/angular";

// Statuses that represent expected application flows, not bugs
const IGNORED_STATUSES = new Set([401, 403, 404, 423]);

// Per-request opt-out for statuses a specific call already treats as an expected outcome
// (e.g. a 400 "link expired" response), without silencing that status for every other endpoint.
export const EXPECTED_ERROR_STATUSES = new HttpContextToken<number[]>(() => []);

@Injectable()
export default class ErrorInterceptorService implements HttpInterceptor {
    // left as 'any' because interceptors are meant to be generic
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((err: HttpErrorResponse) => {
                this.handleSentry(request, err);

                if (err.status === 401) {
                    // auto logout if 401 response returned from api
                    // this.accountService.logout();
                    console.warn("Authentication error occurred.");
                }

                if (err.error && err.error.Message) {
                    return throwError(err.error.Message);
                }
                // This handles the case where we add ModelStateErrors with an empty string as the key
                const errObj = err.error?.hasOwnProperty("") ? err.error[""] : null;

                //this is incase the err contains an error property with a message field
                const error =
                    err.error?.Message || errObj?.length ? errObj : err.error || err.message || err.statusText;
                return throwError(error);
            })
        );
    }

    private handleSentry(req: HttpRequest<any>, err: HttpErrorResponse) {
        // Telemetry must never itself become an uncaught error — e.g. JSON.stringify can throw on a
        // circular err.error (seen on some mobile browsers' network-failure ProgressEvent shapes),
        // which previously escaped this method and reached Angular's global error handler as a raw,
        // unhelpfully-titled HttpErrorResponse capture (duplicating the report below).
        try {
            Sentry.addBreadcrumb({
                category: "http",
                message: `${req.method} ${req.url}`,
                level: "info",
                data: { url: req.url, method: req.method, status_code: err.status },
            });

            if (IGNORED_STATUSES.has(err.status)) return;
            if (req.context.get(EXPECTED_ERROR_STATUSES).includes(err.status)) return;

            // Auth0 session expiry — not an application bug
            if ((err as any)?.error === "login_required" || err?.message === "Login required") return;

            // !err.status covers both status 0 (browser network error) and undefined (client-side
            // request timeout/abort) — neither ever reached the server, so they're not app bugs.
            const level = !err.status || (err.status >= 400 && err.status < 500) ? "warning" : "error";

            // Wrap in a real Error so Sentry titles/groups by endpoint rather than the interceptor call site
            const urlPath = this.normalizeUrlForFingerprint(req.url);
            const syntheticError = new Error(`${req.method} ${urlPath} → ${err.status} ${err.statusText}`);
            syntheticError.name = `HttpError ${err.status}`;

            let responseBody: string;
            try {
                responseBody = typeof err.error === "string" ? err.error : JSON.stringify(err.error);
            } catch {
                responseBody = String(err.error);
            }

            Sentry.captureException(syntheticError, {
                level,
                // Group by method + normalized path + status, not by the interceptor's stack frame
                fingerprint: [req.method, urlPath, String(err.status)],
                tags: {
                    type: "http-error",
                    url: req.url,
                    method: req.method,
                    status: err.status,
                },
                contexts: {
                    request: {
                        url: req.url,
                        method: req.method,
                        headers: this.sanitizeHeaders(req.headers),
                    },
                    response: {
                        status: err.status,
                        statusText: err.statusText,
                        body: responseBody,
                    },
                },
            });
        } catch (telemetryError) {
            console.warn("ErrorInterceptorService.handleSentry failed to report telemetry", telemetryError);
        }
    }

    /** Strip numeric and GUID path segments so /api/loads/123 and /api/loads/456 share a fingerprint.
     *  Versions are always prefixed with v (e.g. /v2/) so pure-digit segments are always IDs. */
    private normalizeUrlForFingerprint(url: string): string {
        const normalize = (path: string) =>
            path
                .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/{id}")
                .replace(/\/\d+/g, "/{id}");
        try {
            return normalize(new URL(url).pathname);
        } catch {
            return normalize(url);
        }
    }

    private sanitizeHeaders(headers: HttpHeaders): Record<string, string> {
        const sensitiveKeys = ["authorization", "x-api-key"];
        const sanitized: Record<string, string> = {};
        headers.keys().forEach((key) => {
            if (!sensitiveKeys.includes(key.toLowerCase())) {
                sanitized[key] = headers.get(key);
            }
        });
        return sanitized;
    }
}
